export function clamp(min, input, max) {
  return Math.max(min, Math.min(input, max))
}

export function lerp(v0, v1, t) {
  return v0 * (1 - t) + v1 * t
}

export function damp(from, to, lambda, dt) {
  return lerp(from, to, 1 - Math.exp(-lambda * dt))
}

export function closest(values, target) {
  return values.reduce((prev, curr) =>
    Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
  )
}

export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect()

  return x >= left && x <= right && y >= top && y <= bottom
}

export function isClickedOutside(event, element) {
  if (
    event.target instanceof HTMLDialogElement ||
    !(event.target instanceof Element)
  ) {
    return !isPointWithinElement(event.clientX, event.clientY, element)
  }

  return !element.contains(event.target)
}

export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback
    : setTimeout

export const requestYieldCallback = (callback) => {
  requestAnimationFrame(() => {
    setTimeout(callback, 0)
  })
}

export function debounce(fn, wait) {
  let timeout

  function debounced(...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn.apply(this, args), wait)
  }

  debounced.cancel = () => {
    clearTimeout(timeout)
  }

  return debounced
}

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')

export function useReduced() {
  return reducedMotion.matches
}

export function useReducedChange(handler) {
  const listener = (event) => handler(event.matches)
  reducedMotion.addEventListener('change', listener)
  return () => reducedMotion.removeEventListener('change', listener)
}

export function onDocumentLoaded(callback) {
  if (document.readyState === 'complete') {
    callback()
  } else {
    window.addEventListener('load', callback)
  }
}

export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback)
  } else {
    callback()
  }
}

export function onAnimationEnd(
  elements,
  callback,
  options = { subtree: true }
) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options)
  const animationPromises = animations.reduce((acc, animation) => {
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished)
    }

    return acc
  }, [])

  return Promise.allSettled(animationPromises).then(callback)
}

export function fetchConfig(type = 'json', config = {}) {
  const headers = {
    Accept: `application/${type}`,
    'Content-Type': 'application/json',
    ...config.headers
  }

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest'
    delete headers['Content-Type']
  }

  return {
    body: config.body,
    headers,
    method: 'POST'
  }
}

/**
 * @param {string} value - CSS time such as `1.618s` or `200ms`
 * @returns {number | null} Milliseconds, or null when the value is not a non-negative CSS time
 */
export function parseCssDuration(value) {
  if (typeof value !== 'string') {
    return null
  }

  const match = /^(\d*\.?\d+)(ms|s)$/.exec(value.trim())
  if (!match) {
    return null
  }

  const amount = Number.parseFloat(match[1])
  return match[2] === 's' ? amount * 1000 : amount
}

/**
 * @param {Element | null} element - Element the custom property is resolved against
 * @param {string} token - Custom property name, such as `--duration`
 * @param {number} fallback - Milliseconds used when the token is absent or malformed
 * @returns {number} Milliseconds
 */
export function readCssDurationMs(element, token, fallback) {
  if (!(element instanceof Element)) {
    return fallback
  }

  const declared = window.getComputedStyle(element).getPropertyValue(token)
  return parseCssDuration(declared) ?? fallback
}
